import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import NotificationBell from "@/components/NotificationBell";
import MessagesBell from "@/components/MessagesBell";
import LanguageChip from "@/components/LanguageChip";
import { BusinessSwitcher } from "@/components/BusinessSwitcher";
import { CartChip } from "@/components/CartChip";
import { useLocality } from "@/contexts/LocalityContext";
import LocalityDialog from "@/components/LocalityDialog";
import { titleCase } from "@/lib/titleCase";
import { Menu, X, LogOut, MapPin, Locate, UserCircle2, Search, ChevronDown, Heart } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useGeo } from "@/lib/useGeo";
import CountryPicker from "@/components/CountryPicker";
import SearchOmnibar from "@/components/search/SearchOmnibar";
import QuickAuthModal from "@/components/QuickAuthModal";
import SocialPulseVerifiedBadge from "@/components/SocialPulseVerifiedBadge";
import { resolveSiteSlug } from "@/config/siteConfig";
import { FP_SECTIONS, FP_SIDE_TABS, FP_QUICK_LINKS } from "@/config/foodPulseSections";

// Site-aware branding. The same Header component is served on
// socialpulse.com, indianfamilytree.com, myfamilytree.care, etc. — the
// logo, wordmark URL, and gradient tagline switch based on which
// sibling site the visitor landed on. Keys resolve via
// ``resolveSiteSlug()``.
const SITE_BRAND = {
  socialpulse: {
    logo: "/assets/social-pulse-logo.jpg",
    logo_alt: "Social Pulse",
    home_url: "https://socialpulse.com",
    home_label: "www.socialpulse.com",
    tagline: "Connecting Communities · Empowering Lives · Building Social Capital",
    tagline_gradient: "linear-gradient(90deg, var(--wofy-primary) 0%, #ec4899 45%, #f59e0b 100%)",
  },
  indianfamilytree: {
    logo: "/assets/ift-logo.png",
    logo_alt: "IndianFamilyTree.com",
    home_url: "https://indianfamilytree.com",
    home_label: "www.indianfamilytree.com",
    tagline: "Discover Your Roots · Preserve Your Legacy · Connect Generations",
    tagline_gradient: "linear-gradient(90deg, #B25D38 0%, #C48A3F 45%, #7C6B57 100%)",
  },
  myfamilytreecare: {
    logo: "/assets/mftcare-logo.png",
    logo_alt: "MyFamilyTree.care",
    home_url: "https://myfamilytree.care",
    home_label: "www.myfamilytree.care",
    tagline: "Honoring Loved Ones · Preserving Memories · Comforting Families",
    tagline_gradient: "linear-gradient(90deg, #0E7C55 0%, #4CAF6A 45%, #C48A3F 100%)",
  },
  foodpulse: {
    logo: "/assets/foodpulse-logo.jpg",
    logo_alt: "Food Pulse",
    home_url: "https://foodpulse.net",
    home_label: "www.foodpulse.net",
    tagline: "Connecting People · Smarter Food · Stronger Communities",
    tagline_gradient: "linear-gradient(90deg, #B25D38 0%, #EA580C 45%, #C79A3F 100%)",
  },
};
function resolveSiteBrand() {
  try {
    const q = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("site")
      : null;
    const slug = (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase();
    if (slug === "foodpulse") return SITE_BRAND.foodpulse;
    if (slug.includes("indianfamilytree")) return SITE_BRAND.indianfamilytree;
    if (slug.includes("myfamilytree")) return SITE_BRAND.myfamilytreecare;
  } catch { /* fall through */ }
  return SITE_BRAND.socialpulse;
}

// Primary nav — Row 1. Site/global actions.
const primaryNav = [
  { to: "/", key: "nav.home", label: "Home", end: true },
  { to: "/explore", key: "nav.explore_places", label: "Explore Places & Topics" },
  { to: "/help", key: "nav.find_offer_help", label: "Find / Offer Help" },
  { to: "/vision-mission", key: "nav.about", label: "About" },
  { to: "/social-capital", key: "nav.social_capital", label: "Social Capital" },
  { to: "/heritage", key: "nav.celebrate", label: "Celebrate a Life" },
  { to: "/heritage", key: "nav.family_tree", label: "Family Tree" },
  { to: "/events", key: "nav.events", label: "Events" },
  { to: "/contact", key: "nav.contact", label: "Contact" },
];

// Obituaries sub-nav — shown as a dedicated horizontal row below the
// primary nav on desktop so visitors don't need to open a dropdown to
// find memorial features. Every entry is also mirrored inside the
// "Obituaries ▾" dropdown on the primary row for people who prefer
// menus. Placed here (rather than inside `primaryNav`) so it can render
// with its own distinctive violet styling on the memorial vertical.
const obituariesNav = [
  { to: "/obituaries/hub",    key: "nav.obituaries_hub",    label: "Obituaries" },
  { to: "/obituaries",        key: "nav.obituaries_recent", label: "Recent Obituaries" },
  { to: "/obituaries/submit", key: "nav.obituaries_submit", label: "Submit Obituary" },
  { to: "/celebrate",         key: "nav.memorial_directory", label: "Memorial Directory" },
  { to: "/family-tree-guide", key: "nav.family_tree_guide", label: "Family Tree Guide" },
];

// Row 2 — life verticals.
const verticalNav = [
  { to: "/food-pulse", key: "nav.food_pulse", label: "Food Pulse" },
  { to: "/organizations", key: "nav.organizations", label: "Organizations" },
  { to: "/realestate", key: "nav.realestate", label: "Real Estate" },
  { to: "/travel", key: "nav.travel", label: "Travel" },
  { to: "/sports", key: "nav.sports", label: "Sports" },
  { to: "/education", key: "nav.education", label: "Education" },
  { to: "/government", key: "nav.government", label: "Government" },
  { to: "/health", key: "nav.health", label: "Health" },
  { to: "/arts", key: "nav.arts", label: "Arts" },
  { to: "/volunteer", key: "nav.volunteer", label: "Volunteer" },
  { to: "/housing", key: "nav.housing", label: "Housing" },
];

// Row 3 — content, community & civic tools.
const contentNav = [
  { to: "/things-to-know", key: "nav.news", label: "News" },
  { to: "/food", key: "nav.food", label: "Food" },
  { to: "/civic", key: "nav.civic_reports", label: "Civic Reports" },
  { to: "/pulse", key: "nav.neighborhood_pulse", label: "Neighborhood Pulse" },
  { to: "/weather", key: "nav.weather", label: "Weather" },
  { to: "/nature", key: "nav.nature", label: "Nature" },
  { to: "/marketplace", key: "nav.marketplace", label: "Marketplace" },
  { to: "/causes", key: "nav.causes", label: "Causes" },
  { to: "/jobs", key: "nav.jobs", label: "Jobs" },
  { to: "/articles", key: "nav.articles", label: "Articles" },
  { to: "/members", key: "nav.members", label: "Members" },
  { to: "/impact-map", key: "nav.impact_map", label: "Impact Map" },
];

// Combined for mobile drawer + legacy reads.
const nav = [...primaryNav, ...obituariesNav, ...verticalNav, ...contentNav];

// Secondary destinations — surfaced through the "More" dropdown.
// Kept intentionally small so the bar is fewer clicks away from any
// major section: Volunteer + Housing Projects were promoted to the
// middle "verticalNav" row.
const secondaryNav = [
  { to: "/world-changers", label: "World Changers" },
  { to: "/stories", label: "Stories" },
];

// ────────────────────────────────────────────────────────────────────
// Child-site thin nav bars.
// When a visitor lands via any child brand (foodpulse.net, kerala.com,
// indias.com, indianfamilytree.com, myfamilytree.care, keralatravels.*)
// we hide the wide 3-row Social Pulse nav and render a single slim
// row of tabs that belong *to that vertical*. Everything else on
// Social Pulse remains reachable via the "More on Social Pulse ▾"
// dropdown on the right.
// ────────────────────────────────────────────────────────────────────
const CHILD_SITE_NAV = {
  foodpulse: {
    accent:  "#B25D38",
    surface: "#F0E2C7",
    // Tabs are lazy-built from FP_SECTIONS at render time so this stays
    // in sync with the Information-Architecture manifest — see
    // `/config/foodPulseSections.js`. See `foodpulseTabsFromManifest()`
    // below the CHILD_SITE_NAV literal.
    dynamic: true,
  },
  kerala: {
    accent:  "#0E7C55",
    surface: "#F0FAF3",
    tabs: [
      { to: "/",              label: "Home", end: true },
      { to: "/explore",       label: "Districts & Places" },
      { to: "/heritage",      label: "Heritage" },
      { to: "/things-to-know",label: "News" },
      { to: "/help",          label: "Community" },
      { to: "/contact",       label: "Contact" },
    ],
  },
  indias: {
    accent:  "#B45309",
    surface: "#FEF3C7",
    tabs: [
      { to: "/",              label: "Home", end: true },
      { to: "/explore",       label: "States & Cities" },
      { to: "/heritage",      label: "Heritage" },
      { to: "/things-to-know",label: "News" },
      { to: "/help",          label: "Community" },
      { to: "/contact",       label: "Contact" },
    ],
  },
  indianfamilytree: {
    accent:  "#B25D38",
    surface: "#FDF6EE",
    tabs: [
      { to: "/",                 label: "Home", end: true },
      { to: "/heritage",         label: "India Family Trees" },
      {
        // Group memorials + obituaries under one "Celebrate a Life" dropdown,
        // matching the live indianfamilytree.com nav.
        label: "Celebrate a Life",
        dropdown: [
          { to: "/celebrate",          label: "Memorial Directory" },
          { to: "/obituaries",         label: "Recent Obituaries" },
          { to: "/obituaries/hub",     label: "Obituaries Hub" },
          { to: "/obituaries/submit",  label: "Submit an Obituary" },
          { divider: true, label: "Family tree" },
          { to: "/family-tree-guide",  label: "Family Tree Guide" },
        ],
      },
      { to: "/famous-families",  label: "Famous Indian Families" },
      { to: "/obituaries",       label: "Obituaries" },
      { to: "/indian-heritage",  label: "Culture & Heritage" },
      {
        // "Explore India" — India-first dropdown surfacing every state
        // (Kerala highlighted, rest alphabetical). Replaces the older
        // "States" pill so there's one entry point for the whole
        // regional atlas.
        label: "Explore India",
        dropdown: [
          { to: "/explore-india",            label: "★ India (all regions)" },
          { divider: true, label: "Featured state" },
          { to: "/state/kerala",             label: "★ Kerala" },
          { divider: true, label: "Other states" },
          { to: "/state/andhra-pradesh",     label: "Andhra Pradesh" },
          { to: "/state/arunachal-pradesh",  label: "Arunachal Pradesh" },
          { to: "/state/assam",              label: "Assam" },
          { to: "/state/bihar",              label: "Bihar" },
          { to: "/state/chhattisgarh",       label: "Chhattisgarh" },
          { to: "/state/delhi",              label: "Delhi" },
          { to: "/state/goa",                label: "Goa" },
          { to: "/state/gujarat",            label: "Gujarat" },
          { to: "/state/haryana",            label: "Haryana" },
          { to: "/state/himachal-pradesh",   label: "Himachal Pradesh" },
          { to: "/state/jammu-and-kashmir",  label: "Jammu & Kashmir" },
          { to: "/state/jharkhand",          label: "Jharkhand" },
          { to: "/state/karnataka",          label: "Karnataka" },
          { to: "/state/ladakh",             label: "Ladakh" },
          { to: "/state/madhya-pradesh",     label: "Madhya Pradesh" },
          { to: "/state/maharashtra",        label: "Maharashtra" },
          { to: "/state/manipur",            label: "Manipur" },
          { to: "/state/meghalaya",          label: "Meghalaya" },
          { to: "/state/mizoram",            label: "Mizoram" },
          { to: "/state/nagaland",           label: "Nagaland" },
          { to: "/state/odisha",             label: "Odisha" },
          { to: "/state/puducherry",         label: "Puducherry" },
          { to: "/state/punjab",             label: "Punjab" },
          { to: "/state/rajasthan",          label: "Rajasthan" },
          { to: "/state/sikkim",             label: "Sikkim" },
          { to: "/state/tamil-nadu",         label: "Tamil Nadu" },
          { to: "/state/telangana",          label: "Telangana" },
          { to: "/state/tripura",            label: "Tripura" },
          { to: "/state/uttar-pradesh",      label: "Uttar Pradesh" },
          { to: "/state/uttarakhand",        label: "Uttarakhand" },
          { to: "/state/west-bengal",        label: "West Bengal" },
        ],
      },
      { to: "/about",            label: "About" },
      { to: "/contact",          label: "Contact" },
    ],
  },
  myfamilytreecare: {
    accent:  "#0E7C55",
    surface: "#F0FAF3",
    tabs: [
      { to: "/",                  label: "Home", end: true },
      { to: "/heritage",          label: "Family Tree" },
      { to: "/obituaries/hub",    label: "Obituaries" },
      { to: "/obituaries/submit", label: "Submit Obituary" },
      { to: "/celebrate",         label: "Memorial Directory" },
      { to: "/family-tree-guide", label: "Family Tree Guide" },
      { to: "/contact",           label: "Contact" },
    ],
  },
  keralatravels: {
    accent:  "#0E7C55",
    surface: "#F0FAF3",
    tabs: [
      { to: "/",         label: "Home", end: true },
      { to: "/travel",   label: "Tours" },
      { to: "/explore",  label: "Destinations" },
      { to: "/help",     label: "Community" },
      { to: "/contact",  label: "Contact" },
    ],
  },
};

/** Materialise Food Pulse tabs from the FP_SECTIONS manifest. Kept
 *  as a function so the manifest stays the single source of truth
 *  for both the nav and the /food-pulse/:section landing pages. */
function foodpulseTabsFromManifest() {
  return FP_SECTIONS.map((s) => {
    // Direct link when the section has a `to` (Home / Articles / Contact).
    if (s.to) {
      return { to: s.to, label: s.label, end: s.end };
    }
    // Dropdown for sections with children — list all children plus a
    // top-level "Overview" link to the section landing.
    if (s.children && s.children.length) {
      return {
        label: s.label,
        dropdown: [
          { to: s.route, label: `All of ${s.label}` },
          ...s.children.map((c) => ({ to: c.to, label: c.label })),
        ],
      };
    }
    // Section with a landing page but no children.
    return { to: s.route, label: s.label };
  });
}

function resolveChildSiteNav() {
  try {
    const q = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("site")
      : null;
    const slug = (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase();
    if (slug && slug !== "socialpulse" && CHILD_SITE_NAV[slug]) {
      const cfg = CHILD_SITE_NAV[slug];
      // Dynamic tab hydration for foodpulse — keeps the huge IA in one
      // manifest instead of duplicating it inside this file.
      const tabs = cfg.tabs || (cfg.dynamic && slug === "foodpulse"
        ? foodpulseTabsFromManifest()
        : []);
      return { slug, ...cfg, tabs };
    }
  } catch { /* fall through */ }
  return null;
}

export const Header = () => {
  const { t } = useTranslation("common", { i18n });
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav_ = useNavigate();
  const { geo, permission, requestPrecise, loading: geoLoading } = useGeo();
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd/Ctrl+K global shortcut to open the search omnibar.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const { locality: userLocality } = useLocality();
  // Prefer the user's *explicitly selected* locality (from LocalityDialog).
  // Fall back to IP/GPS geo when the user hasn't picked one yet.
  const locationParts = userLocality && (userLocality.country || userLocality.state || userLocality.city)
    ? [userLocality.city, userLocality.state, userLocality.country].filter(Boolean).map(titleCase)
    : geo
      ? [geo.city, geo.region, geo.country_name].filter(Boolean).map(titleCase)
      : [];
  const hasLocation = locationParts.length > 0;

  // If the user has manually set locality, show it BOLDLY (vibrant background).
  // If we only have auto-detected geo, use the subtle dark banner + CTA.
  const isManualLocality = userLocality?.source === "manual";
  const manualLocalityParts = isManualLocality
    ? [userLocality.city, userLocality.state, userLocality.country].filter(Boolean).map(titleCase)
    : [];
  const [localityDialogOpen, setLocalityDialogOpen] = useState(false);
  const [quickAuthOpen, setQuickAuthOpen] = useState(false);
  const [quickAuthMode, setQuickAuthMode] = useState("signin");
  const openAuth = (m = "signin") => { setQuickAuthMode(m); setQuickAuthOpen(true); };
  // Site-aware branding — swap logo, wordmark link, and tagline based on
  // whether the visitor is on Social Pulse, IndianFamilyTree, or
  // MyFamilyTree.care. Resolved once on mount because the domain
  // doesn't change during the SPA session.
  const brand = useMemo(resolveSiteBrand, []);
  // Route-aware pathname so pathname-driven memos recompute on navigation.
  const { pathname: currentPathname } = useLocation();
  // ``true`` when we're on any child domain (foodpulse.net, kerala.com,
  // indianfamilytree.com, indias.com, keralatravels.com, myfamilytree.care).
  // Used to suppress the SocialPulse-branded location banner so the child
  // brand owns the top of the viewport.
  const isChildSite = useMemo(() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      const s = (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase();
      if (s && s !== "socialpulse") return true;
      // Cross-site FoodPulse routes (/basket, /pantry, /receipts, /food-pulse/*)
      // own their own cream/green branding, so hide the SocialPulse locality
      // banner too even on the mother-site host.
      if (/^\/(food-pulse|basket|pantry|receipts|smart-basket)(\/|$)/
              .test(currentPathname || "")) {
        return true;
      }
      return false;
    } catch { return false; }
  }, [currentPathname]);
  // Child-site thin-bar config (foodpulse, kerala, indias, IFT, MFT…).
  // ``null`` when we're on the mother Social Pulse — normal 3-row nav
  // renders. When set, we hide primaryNav / obituariesNav sub-strip /
  // sections megamenu and render a single slim tab row instead.
  const childNav = useMemo(resolveChildSiteNav, []);
  // Allow any child component (LocalityHeader, etc.) to open the dialog by
  // dispatching a global event — used by the "Change location" button in the
  // bold banner and by pages that link to /change-location conceptually.
  useEffect(() => {
    const onOpen = () => setLocalityDialogOpen(true);
    window.addEventListener("sp-open-locality-dialog", onOpen);
    return () => window.removeEventListener("sp-open-locality-dialog", onOpen);
  }, []);

  return (
    <>
      <SearchOmnibar open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Prominent BOLD locality bar — shown when the user has set it. */}
      {isManualLocality && (
        <div
          data-testid="location-banner-bold"
          className="w-full"
          style={{
            background: "linear-gradient(90deg, var(--wofy-primary) 0%, #f97316 100%)",
            color: "#FFFFFF",
          }}
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
            <Link
              to="/pulse"
              data-testid="bold-locality-link"
              className="flex items-center gap-3 min-w-0 flex-wrap hover:opacity-90 transition-opacity"
              title="Open your Neighborhood Pulse"
            >
              <MapPin className="w-5 h-5 shrink-0" />
              <span className="text-[10px] uppercase tracking-widest font-mono opacity-80">Your locality</span>
              <span
                data-testid="bold-locality-label"
                className="font-display text-xl sm:text-2xl font-bold tracking-tight"
              >
                {manualLocalityParts.join(" · ")}
              </span>
              <span className="text-[10px] uppercase tracking-widest font-mono opacity-70 hidden sm:inline">
                — view neighborhood pulse
              </span>
            </Link>
          <div className="flex items-center gap-2">
            <a
              href="/blog"
              data-testid="blog-link-topbar"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest rounded-full px-3 py-1.5 hover:bg-white/15 transition-colors whitespace-nowrap"
              style={{ border: "1px solid rgba(255,255,255,0.4)" }}
              title="Explore SocialPulse.blog — writers, places, topics"
            >
              📖 Read the Blog
            </a>
            <button
              data-testid="change-locality-btn"
              onClick={() => setLocalityDialogOpen(true)}
              className="text-xs font-mono uppercase tracking-widest rounded-full px-3 py-1.5 hover:bg-white/15 transition-colors whitespace-nowrap"
              style={{ border: "1px solid rgba(255,255,255,0.4)" }}
            >
              Change location
            </button>
          </div>
        </div>
      </div>
    )}

      {/* Subtle auto-detected banner — shown only when user hasn't set
          manually. Hidden on child sites (foodpulse.net, indianfamilytree.com,
          etc.) so the child brand owns the top of the viewport.
          Slim design: single ~24 px row, no big CTA button — the
          "Locate me" and "Filter by country" actions still work but
          use compact styling so the strip doesn't dominate the page. */}
      {!isManualLocality && !isChildSite && (
      <div
        data-testid="location-banner"
        className="w-full text-xs"
        style={{ background: "var(--wofy-text)", color: "#FDFBF7" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-1 flex items-center justify-between gap-4 leading-tight">
          <div className="inline-flex items-center gap-2 min-w-0 flex-wrap">
            <MapPin className="w-3 h-3 shrink-0" style={{ color: "var(--wofy-primary)" }} />
            {hasLocation ? (
              <>
                <span data-testid="location-label" className="font-mono uppercase tracking-wider">
                  <span className="opacity-60 mr-1 text-[10px]">{t("locality.viewing_from")}</span>
                  <strong className="font-semibold text-xs tracking-normal normal-case">
                    {locationParts.join(", ")}
                  </strong>
                </span>
                {geo?.source === "gps" && (
                  <span className="rounded-full px-1.5 py-px text-[9px] uppercase tracking-wider font-mono" style={{ background: "rgba(253,251,247,0.15)" }}>
                    precise
                  </span>
                )}
              </>
            ) : (
              <span className="opacity-70 text-[11px]">{geoLoading ? "Locating…" : "Location unavailable"}</span>
            )}
            {permission !== "granted" && (
              <button
                type="button"
                onClick={requestPrecise}
                data-testid="use-my-location"
                className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-mono uppercase tracking-wider opacity-80 hover:opacity-100 hover:underline transition-opacity"
                style={{ color: "var(--wofy-primary)" }}
              >
                <Locate className="w-2.5 h-2.5" /> Locate me
              </button>
            )}
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <a
              href="/blog"
              data-testid="blog-link-topbar-slim"
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider opacity-80 hover:opacity-100 hover:underline transition-opacity whitespace-nowrap"
              style={{ color: "var(--wofy-primary)" }}
              title="Explore SocialPulse.blog"
            >
              📖 Read the Blog
            </a>
            <span className="opacity-30 text-[10px]">·</span>
            <span className="text-[10px] font-mono uppercase tracking-wider opacity-60 whitespace-nowrap" data-testid="top-country-caption">
              Country
            </span>
            <CountryPicker compact />
          </div>
        </div>
      </div>
      )}

    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b"
      style={{ background: "rgba(253, 251, 247, 0.85)", backdropFilter: "blur(18px)", borderColor: "var(--wofy-border)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Row 1: Logo + right action buttons */}
        <div className="h-20 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Link
              to="/"
              data-testid="logo-link"
              className="flex flex-col items-center shrink-0"
              aria-label={`${brand.logo_alt} home`}
            >
              <img
                src={brand.logo}
                alt={brand.logo_alt}
                onError={(e) => {
                  // Graceful fallback — if the site-specific logo has not
                  // been uploaded to /public/assets yet, keep the Social
                  // Pulse mark so the header never looks broken.
                  if (e.currentTarget.src.indexOf("social-pulse-logo.jpg") === -1) {
                    e.currentTarget.src = "/assets/social-pulse-logo.jpg";
                  }
                }}
                className="h-14 md:h-16 w-auto object-contain rounded-md"
              />
            </Link>
            <a
              href={brand.home_url}
              target="_self"
              data-testid="logo-url"
              className="hidden md:inline text-sm md:text-base font-bold leading-none hover:underline self-end -ml-2 mb-1.5 shrink-0 tracking-tight"
              style={{ color: "var(--wofy-primary)" }}
            >
              {brand.home_label}
            </a>
            <span
              data-testid="site-tagline"
              className="hidden xl:inline font-display font-bold leading-tight tracking-tight text-lg xl:text-xl 2xl:text-2xl truncate min-w-0 flex-1"
              style={{
                background: brand.tagline_gradient,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {brand.tagline}
            </span>
          </div>

          <div className="hidden lg:flex items-center gap-1.5 shrink-0">
            {user ? (
              <>
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  data-testid="header-search-btn"
                  className="wofy-btn-ghost !px-2.5"
                  aria-label="Search the site"
                  title="Search (⌘K)"
                >
                  <Search className="w-4 h-4" />
                </button>
                {user.is_admin && (
                  <AdminMenu logout={logout} nav_={nav_} />
                )}
                <NotificationBell />
                <MessagesBell />
                <LanguageChip />
                <Link to="/dashboard" data-testid="dashboard-link" className="wofy-btn-ghost">
                  <span className="font-mono text-xs">{user.social_capital ?? 0} pts</span>
                  <span className="ml-2 hidden xl:inline">{user.name.split(" ")[0]}</span>
                  <SocialPulseVerifiedBadge user={user} size="xs" className="ml-1.5 hidden xl:inline-flex" />
                </Link>
                <CartChip />
                {/* Compact profile dropdown — collapses Support / My content /
                    Business switcher / Logout into a single avatar menu so the
                    header row fits standard viewports without horizontal scroll. */}
                <ProfileMenu user={user} logout={logout} nav_={nav_} />
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  data-testid="header-search-btn"
                  className="wofy-btn-ghost !px-2.5"
                  aria-label="Search the site"
                  title="Search (⌘K)"
                >
                  <Search className="w-4 h-4" />
                </button>
                <LanguageChip />
                <button
                  type="button"
                  onClick={() => openAuth("signin")}
                  data-testid="signed-out-bust"
                  className="wofy-btn-ghost !px-2 inline-flex items-center gap-1.5"
                  aria-label="Sign in or register to view your profile"
                  title="Sign in to open your profile"
                >
                  <UserCircle2 className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => openAuth("signin")}
                  data-testid="signin-btn"
                  className="wofy-btn-ghost"
                >
                  {t("header.sign_in")}
                </button>
                <button
                  type="button"
                  onClick={() => openAuth("register")}
                  data-testid="register-btn"
                  className="wofy-btn-primary whitespace-nowrap"
                >
                  <span className="xl:hidden">Join</span>
                  <span className="hidden xl:inline">Join {brand.logo_alt}</span>
                </button>
              </>
            )}
          </div>

          {/* Mobile auth buttons — visible on mobile only.
              Guests see compact Sign in / Join CTAs so they don't have to
              open the hamburger menu just to sign up.
              Signed-in users see a tap-target for their profile avatar. */}
          <div className="lg:hidden flex items-center gap-1.5 shrink-0 ml-auto">
            {user ? (
              <>
                <NotificationBell />
                <MessagesBell />
                <Link
                  to={user.handle ? `/u/${user.handle}` : "/me"}
                  data-testid="mobile-profile-link"
                  className="wofy-btn-ghost !px-2"
                  aria-label="My profile"
                >
                  {(user.avatar_url || user.profile_picture) ? (
                    <img
                      src={user.avatar_url || user.profile_picture}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <UserCircle2 className="w-6 h-6" />
                  )}
                </Link>
                <button
                  data-testid="mobile-logout-btn"
                  onClick={async () => { await logout(); nav_("/"); }}
                  className="wofy-btn-ghost !px-2"
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="mobile-signed-out-bust"
                  className="wofy-btn-ghost !px-2"
                  aria-label="Sign in to open your profile"
                  title="Sign in"
                  onClick={(e) => { e.preventDefault(); openAuth("signin"); }}
                >
                  <UserCircle2 className="w-6 h-6" />
                </Link>
                <Link
                  to="/login"
                  data-testid="mobile-signin-btn"
                  className="wofy-btn-ghost !px-2.5 !py-1.5 text-sm font-medium whitespace-nowrap"
                  onClick={(e) => { e.preventDefault(); openAuth("signin"); }}
                >
                  {t("header.sign_in")}
                </Link>
                <Link
                  to="/register"
                  data-testid="mobile-register-btn"
                  className="wofy-btn-primary !px-3 !py-1.5 text-sm whitespace-nowrap"
                  onClick={(e) => { e.preventDefault(); openAuth("register"); }}
                >
                  <span className="sm:hidden">Join</span>
                  <span className="hidden sm:inline">Join {brand.logo_alt}</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden wofy-btn-ghost !px-2"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Divider line between rows */}
        <div className="hidden lg:block border-t" style={{ borderColor: "var(--wofy-border)" }} />

        {childNav ? (
          /* ────────────── CHILD-SITE THIN NAV BAR ──────────────
             Replaces the full 3-row Social Pulse nav on foodpulse /
             kerala / indias / IFT / MFT / keralatravels. Shows just
             this vertical's own tabs + a "More on Social Pulse ▾"
             dropdown for everything else on the mother site. */
          /* Side tabs strip — only rendered on Food Pulse for now
             ("Explore Social Pulse" + "Nature Pulse" cross-links). */
          <>
            {childNav.slug === "foodpulse" && (
              <div
                className="hidden lg:flex items-center justify-end gap-3 px-4 py-1 border-b"
                data-testid="fp-side-tabs"
                style={{ background: "#FBF7F0", borderColor: "var(--wofy-border)" }}
              >
                {FP_SIDE_TABS.map((t) => (
                  <Link
                    key={t.to}
                    to={t.to}
                    data-testid={`fp-side-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[11px] font-mono uppercase tracking-widest hover:opacity-100 opacity-80 transition-opacity"
                    style={{ color: t.accent }}
                  >
                    {t.label} →
                  </Link>
                ))}
              </div>
            )}
            {childNav.slug === "foodpulse" && (
              <div
                className="hidden md:flex items-center justify-center gap-2 md:gap-3 flex-wrap px-4 py-2 border-b"
                data-testid="fp-quick-links"
                style={{
                  background: "linear-gradient(90deg, #FFF3E0 0%, #FFE9D0 45%, #FFF3E0 100%)",
                  borderColor: "var(--wofy-border)",
                }}
              >
                {FP_QUICK_LINKS.map((q) => (
                  <Link
                    key={q.label}
                    to={q.to}
                    data-testid={`fp-quick-link-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={
                      "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-all hover:shadow-sm hover:-translate-y-[1px] " +
                      (q.emphasis
                        ? "text-white shadow-sm"
                        : "bg-white/70 hover:bg-white")
                    }
                    style={
                      q.emphasis
                        ? { background: q.accent, borderColor: q.accent, color: "#fff" }
                        : { borderColor: q.accent, color: q.accent }
                    }
                  >
                    {q.label}
                    {q.badge && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-widest rounded px-1"
                        style={{ background: q.emphasis ? "rgba(255,255,255,0.28)" : "#EA580C22", color: q.emphasis ? "#fff" : "#EA580C" }}
                      >
                        {q.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          <nav
            className="hidden lg:flex items-center justify-center gap-1 py-1.5 flex-wrap border-b"
            data-testid={`child-thin-nav-${childNav.slug}`}
            style={{ background: childNav.surface, borderColor: "var(--wofy-border)" }}
          >
            <span
              className="text-[10px] font-mono uppercase tracking-widest mr-2 hidden xl:inline"
              style={{ color: childNav.accent }}
              aria-hidden="true"
            >
              {childNav.slug === "foodpulse"        ? "Food Pulse"       :
               childNav.slug === "kerala"           ? "Kerala Pulse"     :
               childNav.slug === "indias"           ? "Indias"           :
               childNav.slug === "indianfamilytree" ? "Indian Family Tree" :
               childNav.slug === "myfamilytreecare" ? "MyFamilyTree.care" :
               childNav.slug === "keralatravels"    ? "Kerala Travels"   : childNav.slug}
            </span>
            {childNav.tabs.map((n) => (
              n.dropdown ? (
                <ChildNavDropdown
                  key={n.label}
                  label={n.label}
                  items={n.dropdown}
                  slug={childNav.slug}
                  accent={childNav.accent}
                />
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${childNav.slug}-${n.to.replace(/\//g, "-").replace(/^-|-$/g, "") || "home"}`}
                  className={({ isActive }) =>
                    `px-3 py-1.5 text-sm rounded-full transition-colors duration-200 whitespace-nowrap ${
                      isActive ? "text-white font-semibold" : "hover:bg-white/70"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive
                      ? { background: childNav.accent, color: "#fff" }
                      : { color: "var(--wofy-text)" }
                  }
                >
                  {n.label}
                </NavLink>
              )
            ))}
            <MoreOnSocialPulseDropdown
              items={[...primaryNav, ...verticalNav, ...contentNav, ...obituariesNav]}
              accent={childNav.accent}
            />
          </nav>
          </>
        ) : (
          <>
            {/* Row 2: primary nav (Home / About / global actions / Contact / Sections dropdown) */}
            <nav className="hidden lg:flex items-center justify-center gap-1 py-2 flex-wrap">
              {primaryNav.map((n) => (
                <NavLink
                  key={n.key || n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.to === "/" ? "home" : n.to.replace(/\//g, "").replace(/^-|-$/g, "")}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm rounded-full transition-colors duration-200 whitespace-nowrap ${isActive ? "bg-[color:var(--wofy-surface)] font-semibold" : "hover:bg-[color:var(--wofy-surface)]"}`
                  }
                  style={{ color: "var(--wofy-text)" }}
                >
                  {t(n.key, n.label)}
                </NavLink>
              ))}
              {/* "Sections" dropdown lives inline in the primary nav
                  instead of occupying its own row below — saves ~50 px
                  of vertical chrome on every page. */}
              <SectionsMegaMenu verticals={verticalNav} content={contentNav} more={secondaryNav} t={t} />
              {/* Obituaries dropdown removed from primary nav per product
                  decision — will be re-linked from the site later. */}
            </nav>

            {/* Row 2b (Obituaries visible sub-nav) removed per product
                decision — memorials will be re-linked from the site
                later. Kept the ``obituariesNav`` data source intact so
                the mega-menu and mobile drawer still work.  */}

            {/* Row 3/4 (own-row Sections menu) removed — `SectionsMegaMenu`
                now renders inline in the primary nav above. */}
          </>
        )}
      </div>

      {open && (
        <div
          className="lg:hidden border-t px-6 py-4 flex flex-col gap-2 overflow-y-auto overscroll-contain"
          style={{
            borderColor: "var(--wofy-border)",
            background: "var(--wofy-bg)",
            // Cap the drawer's height so long menus scroll INSIDE the
            // drawer instead of pushing the page down (which iOS Safari
            // clips when the header is sticky). 3.5rem ≈ the visible
            // top row above the drawer.
            maxHeight: "calc(100vh - 3.5rem)",
          }}
          data-testid="mobile-nav-drawer"
        >
          <div className="pb-2 border-b" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="text-[10px] font-mono uppercase tracking-wider mb-1.5 opacity-60">Viewing content for</div>
            <CountryPicker />
          </div>
          {/* Child site (Food Pulse / IFT / etc.) — render its own tabs
              from the manifest, NOT the SocialPulse global nav. Each tab
              expands to reveal its dropdown children so mobile users
              reach the same destinations as desktop.  */}
          {childNav ? (
            <>
              {childNav.tabs.map((n, idx) => (
                n.dropdown ? (
                  <details key={`${n.label}-${idx}`} className="py-1">
                    <summary className="py-2 text-sm cursor-pointer list-none flex items-center justify-between">
                      <span>{n.label}</span>
                      <ChevronDown className="w-4 h-4 opacity-50" />
                    </summary>
                    <div className="pl-4 pb-1 flex flex-col gap-1">
                      {n.dropdown.map((d) => (
                        <Link key={d.to} to={d.to} onClick={() => setOpen(false)}
                              className="py-1.5 text-sm opacity-80"
                              data-testid={`mobile-child-nav-${d.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}>
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </details>
                ) : (
                  <Link key={`${n.to}-${idx}`} to={n.to} onClick={() => setOpen(false)}
                        className="py-2 text-sm"
                        data-testid={`mobile-child-nav-${(n.to || n.label).toString().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}>
                    {n.label}
                  </Link>
                )
              ))}
              <div className="pt-2 mt-2 border-t text-[10px] font-mono uppercase tracking-wider opacity-60" style={{ borderColor: "var(--wofy-border)" }}>More on Social Pulse</div>
              <Link to="/" onClick={() => setOpen(false)} className="py-2 text-sm">Explore Social Pulse</Link>
            </>
          ) : (
            <>
              {nav.map((n) => (
                <Link key={n.key || n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm">{n.label}</Link>
              ))}
              <div className="pt-2 mt-2 border-t text-[10px] font-mono uppercase tracking-wider opacity-60" style={{ borderColor: "var(--wofy-border)" }}>More</div>
              {secondaryNav.map((n) => (
                <Link key={n.key || n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm">{n.label}</Link>
              ))}
            </>
          )}
          {user ? (
            <>
              {user.is_admin && (
                <Link to="/admin" onClick={() => setOpen(false)} className="wofy-btn-ghost mt-2 justify-center">Admin</Link>
              )}
              <Link to="/dashboard" onClick={() => setOpen(false)} className="wofy-btn-ghost mt-2 justify-center">Dashboard</Link>
              <Link to="/me" onClick={() => setOpen(false)} className="wofy-btn-ghost justify-center">My profile</Link>
              <button onClick={async () => { await logout(); setOpen(false); nav_("/"); }} className="wofy-btn-ghost justify-center">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={(e) => { e.preventDefault(); setOpen(false); openAuth("signin"); }} className="wofy-btn-ghost mt-2 justify-center">Sign in</Link>
              <Link to="/register" onClick={(e) => { e.preventDefault(); setOpen(false); openAuth("register"); }} className="wofy-btn-primary justify-center">Join {brand.logo_alt}</Link>
            </>
          )}
        </div>
      )}
    </header>
    {localityDialogOpen && <LocalityDialog onClose={() => setLocalityDialogOpen(false)} />}
    {quickAuthOpen && <QuickAuthModal mode={quickAuthMode} onClose={() => setQuickAuthOpen(false)} />}
    </>
  );
};


// ObituariesDropdown — a dedicated pill button on the primary nav that
// opens a memorial-focused menu. Rendered ALONGSIDE the visible
// `obituariesNav` sub-strip so people who prefer dropdowns AND people
// who don't know what dropdowns are both find the destinations.
function ObituariesDropdown({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-obituaries-btn"
        className="px-3 py-2 text-sm rounded-full inline-flex items-center gap-1 whitespace-nowrap transition-colors"
        style={{ color: "#7C3AED", background: "rgba(139, 92, 246, 0.08)" }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Heart className="w-3.5 h-3.5 fill-current" />
        Obituaries
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="nav-obituaries-dropdown"
          className="absolute right-0 mt-2 w-64 rounded-xl shadow-lg py-2 z-50"
          style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
        >
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/obituaries"}
              onClick={() => setOpen(false)}
              data-testid={`nav-obituaries-dd-${n.to.replace(/\//g, "-").replace(/^-|-$/g, "")}`}
              className={({ isActive }) =>
                `block px-4 py-2 text-sm ${isActive ? "bg-black/[0.04]" : "hover:bg-black/[0.03]"}`
              }
              style={{ color: "var(--wofy-text)" }}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ChildNavDropdown — reusable dropdown for child-site thin nav tabs.
 * Used e.g. for the "Places ▾" tab on FoodPulse which fans out into
 * Restaurants / Bakeries / Farms / Food Banks / etc.
 */
function ChildNavDropdown({ label, items, slug, accent = "#B25D38" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-${slug}-dropdown-${label.toLowerCase()}-btn`}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap hover:bg-white/70"
        style={{ color: "var(--wofy-text)" }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`nav-${slug}-dropdown-${label.toLowerCase()}`}
          className="absolute left-0 mt-2 w-64 rounded-xl shadow-lg py-2 z-50 max-h-[70vh] overflow-y-auto"
          style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
        >
          {items.map((n, i) => (
            n.divider ? (
              <div
                key={`div-${i}`}
                className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider border-t mt-1 first:mt-0"
                style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}
              >
                {n.label}
              </div>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${slug}-${label.toLowerCase()}-${(n.to || "").replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className="block px-4 py-2 text-sm hover:bg-black/[0.03]"
                style={{ color: "var(--wofy-text)" }}
              >
                {n.label}
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * MoreOnSocialPulseDropdown — the "escape hatch" on child-site thin
 * bars. Exposes every Social Pulse section that got hidden when we
 * swapped the wide nav for the slim child-site tab row, so nothing is
 * ever fully out of reach. Dedupes items by `to` so the same route
 * appearing in multiple nav arrays (e.g. /heritage in primary +
 * verticals) doesn't render twice.
 */
function MoreOnSocialPulseDropdown({ items, accent = "#7C3AED" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);
  const deduped = useMemo(() => {
    const seen = new Set();
    return items.filter((n) => {
      if (!n?.to || seen.has(n.to)) return false;
      seen.add(n.to);
      return true;
    });
  }, [items]);
  return (
    <div className="relative ml-2" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-more-social-pulse-btn"
        className="px-3 py-1.5 text-sm rounded-full inline-flex items-center gap-1 whitespace-nowrap transition-colors border"
        style={{ color: accent, borderColor: accent, background: "#fff" }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        More on Social Pulse
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="nav-more-social-pulse-dropdown"
          className="absolute right-0 mt-2 w-72 rounded-xl shadow-lg py-2 z-50 max-h-[70vh] overflow-y-auto"
          style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
        >
          <div className="px-4 py-1 text-[10px] font-mono uppercase tracking-widest opacity-60">
            Also on Social Pulse
          </div>
          {deduped.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-more-sp-${n.to.replace(/\//g, "-").replace(/^-|-$/g, "") || "home"}`}
              className="block px-4 py-2 text-sm hover:bg-black/[0.03]"
              style={{ color: "var(--wofy-text)" }}
            >
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MoreMenu({ items }) {
  const { t } = useTranslation("common", { i18n });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-more-btn"
        className="px-2.5 py-2 text-sm rounded-full inline-flex items-center gap-1 hover:bg-[color:var(--wofy-surface)] whitespace-nowrap"
        style={{ color: "var(--wofy-text)" }}
      >
        {t("nav.more")} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          data-testid="nav-more-dropdown"
          className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg py-2 z-50"
          style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
        >
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-more-${n.to.replace(/\//g, "").replace(/^-|-$/g, "")}`}
              className={({ isActive }) =>
                `block px-4 py-2 text-sm ${isActive ? "bg-black/[0.04]" : "hover:bg-black/[0.03]"}`
              }
              style={{ color: "var(--wofy-text)" }}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * SectionsMegaMenu — collapses the "life verticals" and "content /
 * community / civic tools" nav rows into a single dropdown button so
 * the sticky header stays compact. Nothing is removed from the site —
 * the same items still ship in the mobile drawer and can still be
 * deep-linked. This is presentation-only, requested to reduce the
 * imposing 4-row header height on Social Pulse.
 */
function SectionsMegaMenu({ verticals = [], content = [], more = [], t }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const label = (n) => (typeof t === "function" ? t(n.key, n.label) : n.label);
  const nav_ = useNavigate();

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        data-testid="sections-mega-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-full border transition-colors hover:bg-[color:var(--wofy-surface)]"
        style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
      >
        <Menu className="w-3.5 h-3.5 opacity-70" />
        Browse all sections
        <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="sections-mega-menu"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[720px] max-w-[calc(100vw-2rem)] rounded-2xl border shadow-xl z-[95] p-5"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card, #fff)" }}
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">Life verticals</div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                {verticals.map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      onClick={() => setOpen(false)}
                      data-testid={`sections-mega-${n.to.replace(/\//g, "").replace(/^-|-$/g, "")}`}
                      className={({ isActive }) =>
                        `block text-sm py-1.5 px-2 rounded-md ${isActive ? "font-semibold bg-[color:var(--wofy-surface)]" : "hover:bg-[color:var(--wofy-surface)]"}`
                      }
                    >
                      {label(n)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">Content, community &amp; civic tools</div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                {content.map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      onClick={() => setOpen(false)}
                      data-testid={`sections-mega-${n.to.replace(/\//g, "").replace(/^-|-$/g, "")}`}
                      className={({ isActive }) =>
                        `block text-sm py-1.5 px-2 rounded-md ${isActive ? "font-semibold bg-[color:var(--wofy-surface)]" : "hover:bg-[color:var(--wofy-surface)]"}`
                      }
                    >
                      {label(n)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {more.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">More</div>
              <ul className="flex flex-wrap gap-1.5">
                {more.map((n) => (
                  <li key={n.to}>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); nav_(n.to); }}
                      className="text-xs px-2.5 py-1 rounded-full border hover:bg-[color:var(--wofy-surface)]"
                      style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
                      data-testid={`sections-mega-more-${n.to.replace(/\//g, "").replace(/^-|-$/g, "")}`}
                    >
                      {n.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AdminMenu — dropdown from the "Admin" badge with quick access to the
 * admin dashboard and a one-click sign-out-and-relogin option. Solves the
 * "I want to see 'Admin', click it, and get out any time" UX.
 */
function AdminMenu({ logout, nav_ }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        data-testid="admin-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="wofy-btn-ghost inline-flex items-center gap-1"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Admin
        <svg className="w-3 h-3 opacity-70" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5 5 5-5H5z"/></svg>
      </button>
      {open && (
        <div
          role="menu"
          data-testid="admin-menu"
          className="absolute right-0 mt-2 w-56 rounded-xl border shadow-lg z-50 p-1"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }}
        >
          <Link
            to="/admin"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
            data-testid="admin-menu-dashboard"
          >
            Admin dashboard
          </Link>
          <Link
            to="/admin?tab=locations"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
            data-testid="admin-menu-locations"
          >
            Locations AI
          </Link>
          <div className="my-1 border-t" style={{ borderColor: "var(--wofy-border)" }} />
          <button
            type="button"
            onClick={async () => { setOpen(false); await logout(); nav_("/signin"); }}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 text-sm text-red-600"
            data-testid="admin-menu-logout"
          >
            Sign out & re-login
          </button>
        </div>
      )}
    </div>
  );
}



/**
 * ProfileMenu — compact avatar-triggered dropdown collapsing the
 * signed-in user's secondary actions (My profile, My content, Support,
 * Business switcher, Logout) into one popover so the header row fits
 * standard viewport widths without horizontal scroll.
 */
function ProfileMenu({ user, logout, nav_ }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const closeAndGo = () => setOpen(false);
  const initials = (user?.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        data-testid="profile-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="wofy-btn-ghost !px-1.5 inline-flex items-center gap-1"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open profile menu"
        title={user?.name || "Profile"}
      >
        {(user?.avatar_url || user?.profile_picture) ? (
          <img
            src={user.avatar_url || user.profile_picture}
            alt=""
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <span
            className="w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-semibold"
            style={{ background: "var(--wofy-primary, #7C3AED)", color: "#fff" }}
          >
            {initials || <UserCircle2 className="w-4 h-4" />}
          </span>
        )}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="profile-menu"
          className="absolute right-0 mt-2 w-64 rounded-xl border shadow-lg z-50 p-1"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }}
        >
          <div className="px-3 py-2 border-b" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="text-sm font-semibold truncate">{user?.name || "Member"}</div>
            {user?.handle && (
              <div className="text-xs opacity-60 truncate">@{user.handle}</div>
            )}
          </div>

          <Link
            to={user?.handle ? `/u/${user.handle}` : "/me"}
            onClick={closeAndGo}
            className="block px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
            data-testid="profile-menu-profile"
          >
            My profile
          </Link>
          <Link
            to="/my-content"
            onClick={closeAndGo}
            className="block px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
            data-testid="profile-menu-content"
          >
            My content
          </Link>
          <Link
            to="/dashboard"
            onClick={closeAndGo}
            className="block px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
            data-testid="profile-menu-dashboard"
          >
            Dashboard <span className="opacity-60">· {user?.social_capital ?? 0} pts</span>
          </Link>

          <div className="my-1 border-t" style={{ borderColor: "var(--wofy-border)" }} />

          {/* Business switcher renders its own inline UI — wrap so it lays out
              nicely inside the menu row. */}
          <div className="px-1 py-0.5" onClick={closeAndGo}>
            <BusinessSwitcher />
          </div>

          <Link
            to="/donate"
            onClick={closeAndGo}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
            style={{ color: "var(--wofy-primary)" }}
            data-testid="profile-menu-support"
          >
            <Heart className="w-4 h-4 fill-current" />
            Support Social Pulse
          </Link>

          <div className="my-1 border-t" style={{ borderColor: "var(--wofy-border)" }} />

          <button
            type="button"
            onClick={async () => { setOpen(false); await logout(); nav_("/"); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-50 text-sm text-red-600"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
